import ShiftCode from "@/components/shift/ShiftCode";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from "react";

export type ShiftCodeCellMode =
  | "weekly_locked"
  | "emergency_locked"
  | "assignment_locked"
  | "violation_messages"
  | "comment_popover"
  | "wanted_mismatch"
  | "default";

export type ShiftCodeCellProps = {
  cellUiMode: ShiftCodeCellMode;
  displayShiftId: string;
  fontSize: string;
  nurseId: string;
  day: number;
  dataShiftId: string;
  dataDefaultShift: string;
  dataShiftGb: string;
  /** 기본(default) 분기에서는 전달하지 않음 → `data-is-error` 생략 */
  dataIsError?: boolean;
  shiftBgColor: string;

  onWeeklyLockedClick: MouseEventHandler<HTMLButtonElement>;
  onEmergencyLockedClick: MouseEventHandler<HTMLButtonElement>;
  onShiftCellClickEditOrFocus: MouseEventHandler<HTMLButtonElement>;
  onMouseEnterCell: MouseEventHandler<HTMLButtonElement>;
  onMouseLeaveCell: MouseEventHandler<HTMLButtonElement>;
  onMouseEnterCommentPopover: MouseEventHandler<HTMLButtonElement>;
  onMouseLeaveCommentPopover: MouseEventHandler<HTMLButtonElement>;

  displayedViolationMessages: string[];

  comment?: string;
  isPopoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  onPopoverContentMouseEnter: () => void;
  onPopoverContentMouseLeave: () => void;

  wantedShiftId?: string;
};

type ShiftCodeDomButtonOwnProps = {
  displayShiftId: string;
  fontSize: string;
  nurseId: string;
  day: number;
  dataShiftId: string;
  dataDefaultShift: string;
  dataShiftGb: string;
  dataIsError?: boolean;
  shiftBgColor: string;
  children?: ReactNode;
};

type ShiftCodeDomButtonProps = ShiftCodeDomButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "title">;

/**
 * `TooltipTrigger` / `PopoverTrigger`의 `asChild`는 ref 병합이 필요함.
 * Radix 툴팁은 열림에 `onPointerMove` / `onPointerLeave` 등을 쓰므로,
 * `onMouseEnter`만 넘기면 안 되고 Trigger가 합쳐 준 나머지 props를 `...rest`로 버튼에 그대로 전달해야 함.
 */
const ShiftCodeDomButton = forwardRef<
  HTMLButtonElement,
  ShiftCodeDomButtonProps
>(function ShiftCodeDomButton(
  {
    className,
    style,
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    dataIsError,
    shiftBgColor,
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn("", className)}
      data-nurse-id={nurseId}
      data-day={day}
      data-shift-id={dataShiftId}
      data-default-shift={dataDefaultShift}
      data-shift-gb={dataShiftGb}
      {...(dataIsError !== undefined
        ? { "data-is-error": dataIsError ? "true" : "false" }
        : {})}
      style={{ ...style }}
      {...rest}
    >
      <ShiftCode code={displayShiftId} color={shiftBgColor} />
      {children}
    </button>
  );
});

/**
 * 근무표 셀 중앙 근무코드 + Tooltip/Popover 분기.
 * 트리거는 `ShiftCodeDomButton`(forwardRef → 실제 `<button>`)로 `asChild`와 호환.
 */
export function ShiftCodeCell(props: ShiftCodeCellProps) {
  const {
    cellUiMode,
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    dataIsError,
    shiftBgColor,
    onWeeklyLockedClick,
    onEmergencyLockedClick,
    onShiftCellClickEditOrFocus,
    onMouseEnterCell,
    onMouseLeaveCell,
    onMouseEnterCommentPopover,
    onMouseLeaveCommentPopover,
    displayedViolationMessages,
    comment,
    isPopoverOpen,
    onPopoverOpenChange,
    onPopoverContentMouseEnter,
    onPopoverContentMouseLeave,
    wantedShiftId,
  } = props;

  const base = {
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    shiftBgColor,
  };

  switch (cellUiMode) {
    case "weekly_locked":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              style={{ cursor: "not-allowed" }}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onWeeklyLockedClick}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>주휴 근무코드는 변경이 불가능합니다.</p>
          </TooltipContent>
        </Tooltip>
      );

    case "violation_messages":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent className="bg-[#444444] text-white whitespace-nowrap">
            <div className="space-y-1">
              <p className="font-semibold text-sm mb-1">위반사항:</p>
              {displayedViolationMessages.map((msg, idx) => (
                <p key={idx} className="text-xs">
                  {msg}
                </p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );

    case "emergency_locked":
      return (
        <ShiftCodeDomButton
          {...base}
          dataIsError={dataIsError}
          style={{ cursor: "not-allowed" }}
          onMouseEnter={onMouseEnterCell}
          onMouseLeave={onMouseLeaveCell}
          onClick={onEmergencyLockedClick}
        />
      );

    case "assignment_locked":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              className="opacity-40"
              style={{ cursor: "not-allowed" }}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>파견 근무는 수정이 불가합니다.</p>
          </TooltipContent>
        </Tooltip>
      );

    case "comment_popover":
      return (
        <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
          <PopoverTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              className="relative"
              dataIsError={dataIsError}
              onMouseEnter={onMouseEnterCommentPopover}
              onMouseLeave={onMouseLeaveCommentPopover}
              onClick={onShiftCellClickEditOrFocus}
            >
              <div className="absolute -right-1 -top-1 bg-red-600 rounded-full border-0 size-2" />
            </ShiftCodeDomButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-3 bg-white border border-gray-200 shadow-lg"
            side="top"
            align="center"
            onMouseEnter={onPopoverContentMouseEnter}
            onMouseLeave={onPopoverContentMouseLeave}
          >
            <div className="space-y-1">
              <p className="text-xs font-semibold text-gray-700 mb-1">
                원티드 신청 메시지
              </p>
              <p className="text-sm text-gray-900">{comment}</p>
            </div>
          </PopoverContent>
        </Popover>
      );

    case "wanted_mismatch":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent className="bg-[#444444]">
            <h1 className="text-xs font-bold! mb-1">근무코드 미일치</h1>
            <p>
              원티드 : {wantedShiftId}
              <br />
              현재 : {displayShiftId}
            </p>
          </TooltipContent>
        </Tooltip>
      );

    case "default":
      return (
        <ShiftCodeDomButton
          {...base}
          onMouseEnter={onMouseEnterCell}
          onMouseLeave={onMouseLeaveCell}
          onClick={onShiftCellClickEditOrFocus}
        />
      );
  }
}
